import { TableConfig } from '@shared/interfaces/table/table-config';
import { ModalConfig } from '@shared/interfaces/modal/modal-config';
import { dashboardColumns, dashboardMeteringPointIdentificationPartial } from '@shared/columns/dashboard.columns';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import {
  dashboardCompleteModal,
  dashboardListModal,
  dashboardReplyModal
} from '@views/dashboard/list-views/util/dashboard-modals.util';
import { dashboardActionMenu } from '@views/dashboard/list-views/util/dashboard.action-menu';
import { alertTypes } from '@shared/interfaces/alert/alert';
import { electricalHeatingColumns } from '@shared/columns/electrical-heating.columns';
import {
  DashboardRemoveConfirmComponent
} from '@shared/modals/dashboard/query/dashboard-remove-confirm/dashboard-remove-confirm.component';
import {
  DashboardDismissUnbalancedQuantityComponent
} from '@shared/modals/dashboard/query/dashboard-dismiss-unbalanced-quantity/dashboard-dismiss-unbalanced-quantity.component';
import { GitPullRequestArrow, HandHelping, Trash } from 'lucide-angular';
import {
  DashboardRequestDataComponent
} from '@shared/modals/dashboard/query/dashboard-request-data/dashboard-request-data.component';
import {
  DashboardQueryManuallyHandledComponent
} from '@shared/modals/dashboard/query/dashboard-query-manually-handled/dashboard-query-manually-handled.component';
import { DashboardService } from '@app/api/dmc';
import { Observable } from 'rxjs';
import { unbalancedQuantityColumns } from '@shared/columns/unbalancedquantity.columns';
import {
  CreateMeterModalComponent
} from '@shared/modals/meteringpoint/meter/create-meter-modal/create-meter-modal.component';
import { ConnectModalComponent } from '@shared/modals/meteringpoint/connect-modal/connect-modal.component';
import { DateCellComponent } from '@shared/components/table/cells/date-cell/date-cell.component';

export type DashboardServiceMethodName = keyof {
  [K in keyof DashboardService as DashboardService[K] extends (params?: any) => Observable<any> ? K : never]: true
};


/**
 * Interface defining the configuration for query dashboard views
 *
 * @interface QueryDashboardConfig
 * @property {string} id - Unique identifier for the dashboard configuration
 * @property {TableConfig<any>} tableConfig - Configuration for the data table display
 * @property {MenuAction[]} [menuActions] - Optional array of menu actions available in the dashboard
 * @property {{ [key: string]: { modalConfig: ModalConfig, activeLength?: number } }} [modals] - Optional modal configurations
 * @property {{ type: alertTypes, message: string }} [alert] - Optional alert configuration with type and message
 */
export interface QueryDashboardConfig {
  // ID and Key
  id?: string,
  // TableConfig
  tableConfig: TableConfig<any>,
  // Nav Items
  menuActions?: MenuAction[],
  // Modals
  modals?: { [key: string]: { modalConfig: ModalConfig, activeLength?: number, params?: any } },
  // Alert
  alert?: { type: alertTypes, message: string }
  // Call Service Internally
  serviceCall?: {
    method: DashboardServiceMethodName,
    params?: any,
    parameterOrder?: string[],
  }
}

/**
 * Default table configuration applied to all dashboard tables
 *
 * @constant {Partial<TableConfig<any>>}
 */
export const queryDashboardTableConfig: Partial<TableConfig<any>> = {
  reorder: true,
  columnSelection: false,
  selectable: true,
  selectableType: 'single',
  saveChanges: true,
  pageSize: 100,
  fetchOnLoad: true,
  exportable: true,
  copyable: true,
  floatingHeader: true,
}

/**
 * Dashboard configuration object that maps URL categories to their respective configurations
 * The object key functions as the appended URL category in /dashboard/query/:category/
 *
 * @constant {{ [key: string]: QueryDashboardConfig }}
 */
export const queryDashboardConfig: { [key: string]: QueryDashboardConfig } = {
  /**
   * Configuration for ordinary disconnection of metering points
   */
  disconnect: {
    id: 'QueryDisconnect',
    serviceCall: {
      method: 'dashboardGetDisconnectGet',
    },
    tableConfig: {
      id: 'table-dashboard-query-disconnect',
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'physicalStatus', 'partyName', 'settlementMethod', 'created', 'occurrence', 'userResponse', 'actionResponse', 'isCancelledBySupplier', 'reasonText'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    menuActions: [
      ...dashboardActionMenu,
    ],
    modals: {
      complete: {
        modalConfig: dashboardCompleteModal,
        activeLength: 1,
        params: {
          actionType: 'disconnect'
        }
      },
      reply: {
        modalConfig: dashboardReplyModal,
        activeLength: 1,
        params: {
          actionType: 'disconnect'
        }
      },
      list: {
        modalConfig: dashboardListModal,
      }
    },
  },

  /**
   * Configuration for disconnection of metering points due to end of supply
   */
  deliverydisconnect: {
    id: 'QueryDisconnectDueToEndOfSupply',
    serviceCall: {
      method: 'dashboardGetDisconnectedDueToEndOfSupplyGet',
    },
    tableConfig: {
      id: 'table-dashboard-query-delivery-disconnect',
      activeColumns: ['meteringPointIdentification', 'address', 'meteringPointType', 'physicalStatus', 'partyName', 'settlementMethod', 'created', 'occurrence', 'actionResponse', 'serviceRequestCode', 'isCancelledBySupplier', 'reasonText'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    menuActions: [
      dashboardActionMenu[1],
      dashboardActionMenu[2],
    ],
    modals: {
      complete: {
        modalConfig: dashboardCompleteModal,
        activeLength: 1,
        params: {
          actionType: 'disconnectDueToEndOfSupply'
        }
      },
      list: {
        modalConfig: dashboardListModal,
      }
    },
  },

  /**
   * Configuration for reopening of metering points
   */
  reopen: {
    id: 'QueryReopen',
    serviceCall: {
      method: 'dashboardGetConnectGet',
    },
    tableConfig: {
      id: 'table-dashboard-query-reopen',
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'physicalStatus', 'partyName', 'created', 'occurrence', 'userResponse', 'actionResponse', 'isCancelledBySupplier', 'reasonText'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    menuActions: [
      ...dashboardActionMenu,
    ],
    modals: {
      complete: {
        modalConfig: dashboardCompleteModal,
        activeLength: 1,
        params: {
          actionType: 'reconnect'
        }
      },
      reply: {
        modalConfig: dashboardReplyModal,
        activeLength: 1,
        params: {
          actionType: 'reconnect'
        }
      },
      list: {
        modalConfig: dashboardListModal,
      }
    },
  },

  /**
   * Configuration for closing down metering points
   */
  closedown: {
    id: 'QueryCloseDown',
    serviceCall: {
      method: 'dashboardGetCloseDownGet',
    },
    tableConfig: {
      id: 'table-dashboard-query-closedown',
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'physicalStatus', 'partyName', 'created', 'occurrence', 'userResponse', 'actionResponse', 'isCancelledBySupplier', 'reasonText'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig
    },
    menuActions: [
      dashboardActionMenu[0],
      dashboardActionMenu[1],
    ],
    modals: {
      complete: {
        modalConfig: dashboardCompleteModal,
        activeLength: 1,
        params: {
          actionType: 'closeDown'
        }
      },
      reply: {
        modalConfig: dashboardReplyModal,
        activeLength: 1,
        params: {
          actionType: 'closeDown'
        }
      },
    },
  },

  /**
   * Configuration for unbalanced energy queries
   */
  unbalancedquantity: {
    id: 'UnbalancedQuantity',
    serviceCall: {
      method: 'dashboardQueryUnbalancedQuantityPost',
      params: {},
    },
    tableConfig: {
      id: 'table-dashboard-unbalanced-quantity',
      columns: unbalancedQuantityColumns,
      ...queryDashboardTableConfig,
    },
    alert: { type: 'info', message: 'dashboard.query.unbalancedquantity.info' },
    menuActions: [
      { id: 'dismiss', title: 'dashboard.query.unbalancedquantity.dismiss', icon: Trash },
    ],
    modals: {
      dismiss: {
        modalConfig: {
          title: 'dashboard.query.unbalancedquantity.remove.title',
          component: DashboardDismissUnbalancedQuantityComponent,
          type: 'center',
          actions: [
            { type: 'primary', label: 'system.button.ok', method: 'submit' },
            { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
          ],
        },
        activeLength: 1,
      },
    },
  },

  /**
   * Configuration for metereddata not created queries
   */
  metereddatanotcreated: {
    id: 'MeteredDataNotCreated',
    serviceCall: {
      method: 'dashboardGetAllMeteredDataNotCreatedGet',
    },
    tableConfig: {
      id: 'table-dashboard-metered-data-not-created',
      columns: dashboardColumns,
      activeColumns: ['meteringPointIdentification', 'dateOfWork'],
      ...queryDashboardTableConfig,
      selectable: false,
    }
  },

  /**
   * Configuration for rejected charge information
   */
  chargeinformationrejected: {
    id: 'QueryChargeInformationRejected',
    serviceCall: {
      method: 'dashboardGetChargeInformationRejectedGet',
    },
    tableConfig: {
      id: 'table-dashboard-charge-information-rejected',
      activeColumns: ['chargeType', 'chargeTypeId', 'description', 'longDescription', 'validFrom', 'validTo', 'observationPeriod', 'ownerEnergyParty', 'dataHubStatus', 'createdBy', 'modifiedBy', 'existsInDataHub'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    modals: {
      manual: {
        modalConfig: {
          title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
          component: DashboardQueryManuallyHandledComponent,
          type: 'middle',
          actions: [
            { type: 'primary', label: 'common.yes', method: 'submit' },
            { type: 'secondary', label: 'common.no', closeOnClick: true },
          ],
        },
        activeLength: 1,
      }
    },
    menuActions: [
      {
        id: 'manual',
        title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
        icon: HandHelping,
      }
    ],
  },

  /**
   * Configuration for pending charge information
   */
  chargeinformationpending: {
    id: 'QueryChargeInformationPending',
    serviceCall: {
      method: 'dashboardGetChargeInformationPendingGet',
    },
    tableConfig: {
      id: 'table-dashboard-charge-information-pending',
      activeColumns: ['chargeType', 'chargeTypeId', 'description', 'longDescription', 'validFrom', 'validTo', 'observationPeriod', 'ownerEnergyParty', 'dataHubStatus', 'createdBy', 'modifiedBy', 'existsInDataHub'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    }
  },

  /**
   * Configuration for failed automations
   */
  failedautomations: {
    id: 'QueryFailedAutomations',
    serviceCall: {
      method: 'dashboardQueryFailedAutomationsPost',
    },
    tableConfig: {
      id: 'table-dashboard-failed-automations',
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'chargeInformationType', 'automationStarted', 'status'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
  },


  /**
   * Configuration for missing electrical heating queries
   */
  missingelectricalheating: {
    id: 'QueryMissingElectricalHeating',
    serviceCall: {
      method: 'dashboardGetMissingElectricalHeatingGet',
    },
    tableConfig: {
      id: 'table-dashboard-electricalheating-missing',
      columns: electricalHeatingColumns,
      ...queryDashboardTableConfig,
      selectable: false,
    },
  },

  /**
   * Configuration for removal of electrical heating queries
   */
  removalelectricalheating: {
    id: 'QueryRemovalOfElectricalHeating',
    serviceCall: {
      method: 'dashboardGetRemovalOfElectricalHeatingGet',
    },
    tableConfig: {
      id: 'table-dashboard-electricalheating-remove',
      columns: electricalHeatingColumns,
      ...queryDashboardTableConfig,
      selectable: false,
    },
  },

  /**
   * Configuration for changes due to production obligation
   */
  changestoproductionobligation: {
    id: 'QueryChangesToProductionObligation',
    serviceCall: {
      method: 'dashboardGetChangesToProductionObligationGet',
    },
    tableConfig: {
      id: 'table-dashboard-changes-to-production-obligation',
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'productionObligation', 'occurrence', 'created'],
      columns: [
        { title: 'chargeInformation.occurrence', key: 'occurrence', cellRenderer: DateCellComponent },
        { title: 'dashboard.table.receivingtime', key: 'created', cellRenderer: DateCellComponent },
        ...dashboardColumns,
      ],
      ...queryDashboardTableConfig,
    },
    modals: {
      remove: {
        modalConfig: {
          title: 'common.remove',
          component: DashboardRemoveConfirmComponent,
          type: 'middle',
          actions: [
            { type: 'primary', label: 'system.button.ok', method: 'submit' },
            { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
          ],
        },
        activeLength: 1
      },
    },
    menuActions: [
      {
        id: 'remove',
        title: 'common.remove',
        icon: Trash,
      },
    ]
  },

  /**
   * Configuration for new metering points with supplier
   */
  newmeteringpointwithsupplier: {
    id: 'QueryNewMeteringPointWithSupplier',
    serviceCall: {
      method: 'dashboardGetNewMeteringPointsWithSupplierGet',
    },
    tableConfig: {
      id: 'table-dashboard-new-metering-point-with-supplier',
      activeColumns: ['meteringPointIdentification', 'address', 'physicalStatusOfMeteringPoint', 'typeOfMeteringPoint', 'subTypeOfMeteringPoint', 'meterNumber', 'dataHubStatus', 'balanceSupplierDate'],
      columns: dashboardColumns,
      selectable: false,
      ...queryDashboardTableConfig,
    },
    modals: {
      createMeter: {
        modalConfig: {
          component: CreateMeterModalComponent,
          title: 'meteringpoint.meter.create.title',
          actions: [
            { type: 'primary', label: 'common.create', method: 'submit' },
            { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
          ],
        },
        activeLength: 1,
      },
      connect: {
        modalConfig: {
          component: ConnectModalComponent,
          title: 'common.connect',
          actions: [
            { type: 'primary', label: 'common.connect', method: 'submit' },
            { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
          ],
        },
        activeLength: 1,
      }
    },
    menuActions: [
      {
        id: 'createMeter',
        title: 'meteringpoint.meter.create.title'
      },
      {
        id: 'connect',
        title: 'common.connect',
      }
    ]
  },

  /**
   * Configuration for meteringpoint charge information rejected
   */
  meteringpointchargeinformationrejected: {
    id: 'QueryMeteringPointChargeInformationRejected',
    serviceCall: {
      method: 'dashboardGetMeteringPointsChargeInformationRejectedGet',
    },
    tableConfig: {
      id: 'table-dashboard-metering-point-charge-information-rejected',
      activeColumns: ['meteringPointIdentification', 'chargeType', 'chargeInformationIdentification', 'validFrom', 'validTo', 'chargeOccurrences', 'dataHubStatus', 'statusReasonCode'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    modals: {
      manual: {
        modalConfig: {
          title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
          component: DashboardQueryManuallyHandledComponent,
          type: 'middle',
          actions: [
            { type: 'primary', label: 'common.yes', method: 'submit' },
            { type: 'secondary', label: 'common.no', closeOnClick: true },
          ],
        },
        activeLength: 1,
      }
    },
    menuActions: [
      {
        id: 'manual',
        title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
        icon: HandHelping,
      }
    ],
  },

  /**
   * Configuration for meteringpoint charge information pending
   */
  meteringpointchargeinformationpending: {
    id: 'QueryMeteringPointChargeInformationPending',
    serviceCall: {
      method: 'dashboardGetMeteringPointsChargeInformationPendingGet',
    },
    tableConfig: {
      id: 'table-dashboard-metering-point-charge-information-rejected',
      activeColumns: ['meteringPointIdentification', 'chargeType', 'chargeInformationIdentification', 'validFrom', 'validTo', 'chargeOccurrences', 'dataHubStatus'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
  },

  /**
   * Configuration for meteringpoints rejected by DataHub
   */
  rejectedmeteringpoints: {
    id: 'QueryRejectedMeteringPoints',
    serviceCall: {
      method: 'dashboardGetMeteringPointsRejectedByDataHubGet',
      params: {
        showHandled: false,
      },
      parameterOrder: ['page', 'pageSize', 'showHandled'],
    },
    tableConfig: {
      id: 'table-dashboard-query-rejectedmeteringpoints',
      activeColumns: ['meteringPointIdentification', 'address', 'settlementMethod', 'typeOfMeteringPoint', 'physicalStatusOfMeteringPoint', 'dataHubStatus', 'isChild', 'hasBalanceSupplier', 'existsInDataHub'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
    modals: {
      manual: {
        modalConfig: {
          title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
          component: DashboardQueryManuallyHandledComponent,
          type: 'middle',
          actions: [
            { type: 'primary', label: 'common.yes', method: 'submit' },
            { type: 'secondary', label: 'common.no', closeOnClick: true },
          ],
        },
        activeLength: 1,
      }
    },
    menuActions: [
      {
        id: 'manual',
        title: 'dashboard.query.meteringpointchargeinformationrejected.manuallyhandled',
        icon: HandHelping,
      }
    ],
  },

  /**
   * Configuration for meters not in EnergyConnect (DHM)
   */
  notindhm: {
    id: 'QueryMetersNotInDHM',
    serviceCall: {
      method: 'dashboardGetMeteringPointsNotInDHMGet',
    },
    tableConfig: {
      id: 'table-dashboard-not-in-DHM',
      columns: dashboardColumns.map(col =>
        col.key === 'meteringPointIdentification'
          ? { ...dashboardMeteringPointIdentificationPartial, cellRenderer: undefined, cellRendererParams: undefined }
          : col
      ),
      activeColumns: ['meteringPointIdentification', 'messagesCount', 'oldestDayOfWork'],
      ...queryDashboardTableConfig,
    },
    modals: {
      request: {
        modalConfig: {
          title: 'dashboard.query.notindhm.request',
          component: DashboardRequestDataComponent,
          actions: [
            { type: 'primary', label: 'system.button.request', method: 'submit' },
            { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
          ],
        },
        activeLength: 1,
      }
    },
    menuActions: [
      {
        id: 'request',
        title: 'dashboard.query.notindhm.request',
        icon: GitPullRequestArrow,
      }
    ],
  },

  /**
   * Configuration for active meters not connected to meter frame
   */
  metersnotconnected: {
    id: 'QueryMetersNotConnected',
    serviceCall: {
      method: 'dashboardActiveMetersNotConnectedToMeterFrameGet',
    },
    tableConfig: {
      id: 'table-dashboard-meters-not-connected',
      columns: dashboardColumns,
      activeColumns: ['serialNumber', 'meterNumber', 'quadrant', 'alternateMeterNumber', 'type'],
      ...queryDashboardTableConfig,
      selectable: false,
    }
  },

  /**
   * Configuration for ghost meters (Meters only known by EC)
   */
  ghostmeters: {
    id: 'QueryGhostMeters',
    serviceCall: {
      method: 'dashboardGetGhostMetersGet',
    },
    tableConfig: {
      id: 'table-dashboard-ghost-meters',
      columns: dashboardColumns,
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'connectivityStatus', 'observationPeriod', 'meterNumber', 'quadrant'],
      ...queryDashboardTableConfig,
      selectable: false,
    }
  },

  /**
   * Configuration for metering points without a meter
   */
  nometer: {
    id: 'QueryPhyscalMeteringPointNoMeter',
    serviceCall: {
      method: 'dashboardGetPhysicalMeteringPointsWithoutMetersGet',
    },
    tableConfig: {
      id: 'table-dashboard-physical-metering-point-no-meter',
      columns: dashboardColumns,
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'subTypeOfMeteringPoint', 'physicalStatusOfMeteringPoint', 'validFrom', 'settlementMethod', 'dataHubStatus'],
      ...queryDashboardTableConfig,
      selectable: false,
    }
  },

  /**
   * Configuration for metering points with overdue pending status
   */
  meteringpointsoverduepending: {
    id: 'QueryMeteringPointsOverduePending',
    serviceCall: {
      method: 'dashboardGetMeteringPointsOverduePendingGet',
    },
    tableConfig: {
      id: 'table-dashboard-query-meteringpoints-overdue-pending',
      activeColumns: ['meteringPointIdentification', 'address', 'settlementMethod', 'typeOfMeteringPoint', 'physicalStatusOfMeteringPoint', 'dataHubStatus', 'isChild', 'hasBalanceSupplier', 'existsInDataHub'],
      columns: dashboardColumns,
      ...queryDashboardTableConfig,
    },
  },

  /**
   * Configuration for virtual metering points without calculations
   */
  virtualnocalculation: {
    id: 'QueryVirtuelMeteringPointNoCalculationStructure',
    serviceCall: {
      method: 'dashboardGetVirtualMeteringPointsNoCalculationGet',
    },
    tableConfig: {
      id: 'table-dashboard-physical-virtual-metering-point-no-calculation-structure',
      columns: dashboardColumns,
      activeColumns: ['meteringPointIdentification', 'address', 'typeOfMeteringPoint', 'subTypeOfMeteringPoint', 'physicalStatusOfMeteringPoint', 'validFrom', 'dataHubStatus'],
      ...queryDashboardTableConfig,
      selectable: false,
    }
  },
}
